import { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useSnackbar } from "notistack";

import { LANGUAGES, LanguageCode, canReloadOffline } from "../../i18n/languages";
import { chooseLanguage } from "../../i18n/languageChoice";
import { isOnlineNow } from "../../offline/connectivity";
import { RootState } from "../../state/store";

export function HeaderLanguage() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const userId = useSelector((state: RootState) => state.authReducer.user?.id);

  const choose = (code: LanguageCode) => {
    if (!isOnlineNow() && !canReloadOffline()) {
      enqueueSnackbar(t("header.languageNeedsConnection"), { variant: "warning", autoHideDuration: 5000 });
      return;
    }
    void chooseLanguage(code, { savingTo: userId });
  };

  return (
    <>
      <Tooltip title={t("header.language")} arrow>
        <button
          type="button"
          aria-haspopup="true"
          aria-label={t("header.language")}
          onClick={e => setAnchor(e.currentTarget)}
          className="flex items-center gap-1 transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer"
        >
          <FontAwesomeIcon icon={faGlobe} className="!w-5 !h-5 compact:!w-4 compact:!h-4" />
          <span className="text-xs font-semibold uppercase">{i18n.language.slice(0, 2)}</span>
        </button>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <ul aria-label={t("header.language")} className="flex flex-col p-1 bg-theme-surface-2 border border-theme-border">
          {LANGUAGES.map(language => (
            <li key={language.code}>
              <button
                type="button"
                lang={language.code}
                aria-current={language.code === i18n.language ? "true" : undefined}
                onClick={() => {
                  setAnchor(null);
                  if (language.code !== i18n.language) choose(language.code);
                }}
                className={`w-full text-left px-3 py-1 text-sm rounded cursor-pointer hover:bg-theme-surface ${language.code === i18n.language ? "font-semibold text-theme-focus" : "text-theme-text"}`}
              >
                {language.name}
              </button>
            </li>
          ))}
        </ul>
      </Popover>
    </>
  );
}
